import {
  Award,
  Calendar,
  CheckCircle,
  Download,
  Gift,
  QrCode,
  Star,
  Users,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";
import { parentService, recompenseService } from "../services/api";

const ParentDashboard = () => {
  const { user } = useAuth();
  const [parent, setParent] = useState(null);
  const [carteTampons, setCarteTampons] = useState(null);
  const [historique, setHistorique] = useState([]);
  const [recompenses, setRecompenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qrData, setQrData] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);

  useEffect(() => {
    fetchParentData();
  }, []);

  const fetchParentData = async () => {
    try {
      setLoading(true);

      // Données de démonstration
      const demoParent = {
        id: 1,
        user: { nom: user?.nom || "Marie Mbarga" },
        nom_enfant: "Sophie Mbarga",
        classe_enfant: "4ème A",
        points_total: 150,
        niveau_fidelite: "Argent",
        telephone: "+237 123 456 789",
      };

      const demoCarteTampons = {
        tampons_obtenus: 7,
        tampons_max: 10,
        progression_pourcentage: 70,
        prochaine_recompense: "Certificat de participation",
      };

      const demoHistorique = [
        {
          id: 1,
          activite: { nom: "Réunion parents", type_activite: "Réunion" },
          points_obtenus: 10,
          date_validation: new Date().toISOString(),
        },
        {
          id: 2,
          activite: { nom: "Sortie éducative", type_activite: "Sortie" },
          points_obtenus: 15,
          date_validation: new Date(Date.now() - 86400000).toISOString(),
        },
        {
          id: 3,
          activite: { nom: "Atelier cuisine", type_activite: "Atelier" },
          points_obtenus: 20,
          date_validation: new Date(Date.now() - 172800000).toISOString(),
        },
      ];

      const demoRecompenses = [
        {
          id: 1,
          recompense: {
            titre: "Certificat Bronze",
            type_recompense: "Certificat",
          },
          telecharge: false,
        },
        {
          id: 2,
          recompense: {
            titre: "Badge Participation",
            type_recompense: "Badge",
          },
          telecharge: true,
        },
      ];

      // Essayer de récupérer les vraies données d'abord
      try {
        const parentResponse = await parentService.getMyParentData();
        const parentData = parentResponse.data;
        console.log("Parent data from API:", parentData);

        if (parentData) {
          setParent(parentData);

          const [carteResponse, historiqueResponse, recompensesResponse] =
            await Promise.all([
              parentService.getCarteTampons(parentData.id),
              parentService.getHistorique(parentData.id),
              recompenseService.getParentRecompenses(parentData.id),
            ]);

          console.log("Carte response:", carteResponse.data);
          console.log("Historique response:", historiqueResponse.data);
          console.log("Recompenses response:", recompensesResponse.data);

          setCarteTampons(carteResponse.data);
          setHistorique(historiqueResponse.data.historique || []);
          setRecompenses(recompensesResponse.data);
        }
      } catch (apiError) {
        console.error("API Error:", apiError);
        console.log(
          "API non disponible, utilisation des données de démonstration"
        );

        // Utiliser les données de démonstration en cas d'erreur
        setParent(demoParent);
        setCarteTampons(demoCarteTampons);
        setHistorique(demoHistorique);
        setRecompenses(demoRecompenses);
      }
    } catch (error) {
      console.error("Error fetching parent data:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const handleShowQR = async () => {
    try {
      console.log("Fetching QR code for parent ID:", parent.id);
      const response = await parentService.getParentQR(parent.id);
      console.log("QR code response:", response.data);
      setQrData(response.data);
      setShowQRModal(true);
    } catch (error) {
      console.error("Error fetching QR code:", error);
      toast.error("Erreur lors du chargement du QR code");

      // Fallback avec des données de démonstration
      const demoQRData = {
        qr_code: "DEMO_QR_CODE_12345",
        qr_image: null, // Pas d'image pour le démo
        parent_name: parent?.nom || "Parent",
        enfant: parent?.nom_enfant || "Enfant",
      };
      setQrData(demoQRData);
      setShowQRModal(true);
    }
  };

  const handleExportTampons = () => {
    if (!historique || historique.length === 0) {
      toast.error("Aucune activité à exporter");
      return;
    }

    // Créer le contenu du document
    const exportData = {
      parent: parent?.nom || "Parent",
      enfant: parent?.nom_enfant || "Enfant",
      classe: parent?.classe_enfant || "Classe",
      points_total: parent?.points_total || 0,
      niveau: parent?.niveau_fidelite || "Bronze",
      date_export: new Date().toLocaleDateString("fr-FR"),
      activites: historique.map((tampon) => ({
        activite: tampon.activite.nom,
        type: tampon.activite.type_activite,
        points: tampon.points_obtenus,
        date: new Date(tampon.date_validation).toLocaleDateString("fr-FR"),
        commentaire: tampon.commentaire || "",
      })),
    };

    // Créer le contenu HTML
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Mes Activités Validées - ${exportData.parent}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { text-align: center; margin-bottom: 30px; }
          .info { background: #f5f5f5; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          .activity { border: 1px solid #ddd; padding: 10px; margin: 10px 0; border-radius: 5px; }
          .points { color: #059669; font-weight: bold; }
          .footer { text-align: center; margin-top: 30px; color: #666; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Mes Activités Validées</h1>
          <h2>${exportData.parent} - Parent de ${exportData.enfant}</h2>
        </div>
        
        <div class="info">
          <p><strong>Enfant:</strong> ${exportData.enfant}</p>
          <p><strong>Classe:</strong> ${exportData.classe}</p>
          <p><strong>Points totaux:</strong> ${exportData.points_total}</p>
          <p><strong>Niveau:</strong> ${exportData.niveau}</p>
          <p><strong>Date d'export:</strong> ${exportData.date_export}</p>
        </div>
        
        <h3>Activités validées (${exportData.activites.length})</h3>
        ${exportData.activites
          .map(
            (activite) => `
          <div class="activity">
            <h4>${activite.activite}</h4>
            <p><strong>Type:</strong> ${activite.type}</p>
            <p><strong>Points:</strong> <span class="points">+${
              activite.points
            }</span></p>
            <p><strong>Date:</strong> ${activite.date}</p>
            ${
              activite.commentaire
                ? `<p><strong>Commentaire:</strong> ${activite.commentaire}</p>`
                : ""
            }
          </div>
        `
          )
          .join("")}
        
        <div class="footer">
          <p>Document généré le ${
            exportData.date_export
          } par e-Tampon Parent Connect</p>
        </div>
      </body>
      </html>
    `;

    // Créer et télécharger le fichier
    const blob = new Blob([htmlContent], { type: "text/html" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mes-activites-${
      exportData.parent
    }-${exportData.date_export.replace(/\//g, "-")}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    toast.success("Document exporté avec succès !");
  };

  if (loading) {
    return (
      <div className="min-h-full flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-200 rounded-full animate-spin border-t-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            Chargement de votre tableau de bord...
          </p>
        </div>
      </div>
    );
  }

  if (!parent) {
    return (
      <div className="min-h-full flex items-center justify-center">
        <div className="text-center bg-white rounded-lg shadow-sm p-8 max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Profil parent non trouvé
          </h2>
          <p className="text-gray-600">
            Contactez l'administration pour créer votre profil parent.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Bonjour, {user.nom} ! 👋
            </h1>
            <p className="text-gray-600 mt-1">
              Parent de {parent.nom_enfant} - {parent.classe_enfant}
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleShowQR}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              <QrCode className="h-5 w-5" />
              <span>Mon QR Code</span>
            </button>

            {/* Code unique */}
            <div className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg">
              <span className="text-sm font-medium">Code unique:</span>
              <span className="font-mono text-lg font-bold">
                {parent?.code_unique || "N/A"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Points Total */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Star className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Points Total</p>
              <p className="text-2xl font-bold text-gray-900">
                {parent.points_total}
              </p>
            </div>
          </div>
        </div>

        {/* Niveau de Fidélité */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Award className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Niveau</p>
              <p className="text-2xl font-bold text-gray-900">
                {parent.niveau_fidelite}
              </p>
            </div>
          </div>
        </div>

        {/* Tampons Obtenus */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Calendar className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Tampons</p>
              <p className="text-2xl font-bold text-gray-900">
                {carteTampons?.tampons_obtenus || 0}/
                {carteTampons?.tampons_max || 10}
              </p>
            </div>
          </div>
        </div>

        {/* Récompenses */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <Gift className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Récompenses</p>
              <p className="text-2xl font-bold text-gray-900">
                {recompenses.length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Carte Tampons - 2 colonnes */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">
                Ma Carte Tampons
              </h2>
              <span className="text-sm text-gray-500">
                {carteTampons?.tampons_obtenus || 0} /{" "}
                {carteTampons?.tampons_max || 10}
              </span>
            </div>

            {/* Carte à tampons visuelle */}
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 border-2 border-blue-200 mb-6">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  Carte de Fidélité Parent
                </h3>
                <p className="text-sm text-gray-600">
                  {parent?.nom} - Parent de {parent?.nom_enfant}
                </p>
              </div>

              {/* Grille de tampons */}
              <div className="grid grid-cols-5 gap-3 mb-6">
                {Array.from(
                  { length: carteTampons?.tampons_max || 10 },
                  (_, index) => {
                    const isStamped =
                      index < (carteTampons?.tampons_obtenus || 0);
                    return (
                      <div
                        key={index}
                        className={`aspect-square rounded-lg border-2 flex items-center justify-center transition-all duration-300 ${
                          isStamped
                            ? "bg-gradient-to-br from-green-400 to-emerald-500 border-green-500 shadow-lg transform scale-105"
                            : "bg-white border-gray-300 hover:border-gray-400"
                        }`}
                      >
                        {isStamped ? (
                          <div className="text-white">
                            <CheckCircle className="h-8 w-8" />
                          </div>
                        ) : (
                          <div className="text-gray-400">
                            <div className="w-6 h-6 border-2 border-dashed border-gray-300 rounded-full"></div>
                          </div>
                        )}
                      </div>
                    );
                  }
                )}
              </div>

              {/* Barre de progression */}
              <div className="mb-4">
                <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all duration-1000"
                    style={{
                      width: `${carteTampons?.progression_pourcentage || 0}%`,
                    }}
                  ></div>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Progression</span>
                  <span>{carteTampons?.progression_pourcentage || 0}%</span>
                </div>
              </div>

              {/* Prochaine récompense */}
              {carteTampons?.prochaine_recompense && (
                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-4 border border-yellow-200">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                      <Award className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-orange-800">
                        Prochaine récompense
                      </p>
                      <p className="text-sm text-orange-600">
                        {carteTampons.prochaine_recompense}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Informations sur les tampons */}
            <div className="grid grid-cols-2 gap-4 text-center">
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-2xl font-bold text-blue-600">
                  {carteTampons?.tampons_obtenus || 0}
                </p>
                <p className="text-sm text-blue-800">Tampons obtenus</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-3">
                <p className="text-2xl font-bold text-purple-600">
                  {(carteTampons?.tampons_max || 10) -
                    (carteTampons?.tampons_obtenus || 0)}
                </p>
                <p className="text-sm text-purple-800">Tampons restants</p>
              </div>
            </div>
          </div>
        </div>

        {/* Récompenses - 1 colonne */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">
              Mes Récompenses
            </h2>

            {recompenses.length > 0 ? (
              <div className="space-y-4">
                {recompenses.slice(0, 3).map((recompense) => (
                  <div
                    key={recompense.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                        <Award className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 text-sm">
                          {recompense.recompense.titre}
                        </p>
                        <p className="text-xs text-gray-600">
                          {recompense.recompense.type_recompense}
                        </p>
                      </div>
                    </div>
                    {!recompense.telecharge && (
                      <button className="text-purple-600 hover:text-purple-700 p-1 rounded hover:bg-purple-100 transition-colors duration-200">
                        <Download className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Gift className="h-6 w-6 text-gray-400" />
                </div>
                <p className="text-gray-600 font-medium text-sm mb-1">
                  Aucune récompense
                </p>
                <p className="text-xs text-gray-500">
                  Participez aux activités !
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mes Tampons - Cartes individuelles */}
      <div className="mt-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">
            Mes Tampons
          </h2>

          {historique.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {historique.map((tampon) => (
                <div
                  key={tampon.id}
                  className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border-2 border-green-200 hover:shadow-lg transition-all duration-300"
                >
                  {/* En-tête de la carte tampon */}
                  <div className="text-center mb-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-2 shadow-lg">
                      <CheckCircle className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="font-bold text-gray-800 text-sm">
                      TAMPON VALIDÉ
                    </h3>
                  </div>

                  {/* Contenu de la carte */}
                  <div className="space-y-2">
                    <div className="text-center">
                      <p className="font-semibold text-gray-900 text-lg">
                        {tampon.activite.nom}
                      </p>
                      <p className="text-sm text-gray-600">
                        {tampon.activite.type_activite}
                      </p>
                    </div>

                    <div className="flex justify-between items-center py-2 border-t border-green-200">
                      <span className="text-sm text-gray-600">Points:</span>
                      <span className="font-bold text-green-600 text-lg">
                        +{tampon.points_obtenus}
                      </span>
                    </div>

                    <div className="flex justify-between items-center py-2 border-t border-green-200">
                      <span className="text-sm text-gray-600">Date:</span>
                      <span className="text-sm font-medium text-gray-800">
                        {new Date(tampon.date_validation).toLocaleDateString(
                          "fr-FR"
                        )}
                      </span>
                    </div>

                    {tampon.commentaire && (
                      <div className="pt-2 border-t border-green-200">
                        <p className="text-xs text-gray-600 italic">
                          "{tampon.commentaire}"
                        </p>
                      </div>
                    )}

                    {/* Signature/Validation */}
                    <div className="pt-2 border-t border-green-200 text-center">
                      <p className="text-xs text-gray-500">
                        Validé par l'administration
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-10 w-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Aucun tampon obtenu
              </h3>
              <p className="text-gray-600 mb-4">
                Participez aux activités scolaires pour obtenir vos premiers
                tampons !
              </p>
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-50 rounded-lg border border-blue-200">
                <QrCode className="h-4 w-4 text-blue-600" />
                <span className="text-sm text-blue-800">
                  Présentez votre QR code aux événements
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Historique des Activités */}
      <div className="mt-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">
              Mes Activités Validées
            </h2>
            <button
              onClick={handleExportTampons}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
            >
              <Download className="h-4 w-4" />
              <span>Exporter</span>
            </button>
          </div>

          {historique.length > 0 ? (
            <div className="space-y-3">
              {historique.map((tampon) => (
                <div
                  key={tampon.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                      <CheckCircle className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {tampon.activite.nom}
                      </p>
                      <p className="text-sm text-gray-600">
                        {tampon.activite.type_activite} •{" "}
                        {new Date(tampon.date_validation).toLocaleDateString(
                          "fr-FR"
                        )}
                      </p>
                      {tampon.commentaire && (
                        <p className="text-xs text-gray-500 mt-1">
                          "{tampon.commentaire}"
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-green-600">
                      +{tampon.points_obtenus} pts
                    </p>
                    <p className="text-xs text-gray-500">Validé</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Calendar className="h-6 w-6 text-gray-400" />
              </div>
              <p className="text-gray-600 font-medium text-sm mb-1">
                Aucune activité validée
              </p>
              <p className="text-xs text-gray-500">
                Participez aux événements scolaires pour voir vos activités ici
                !
              </p>
            </div>
          )}
        </div>
      </div>

      {/* QR Code Modal */}
      {showQRModal && qrData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                Votre QR Code
              </h3>
              <div className="bg-white p-4 rounded-lg border-2 border-gray-200 inline-block mb-6">
                {qrData.qr_image ? (
                  <img
                    src={qrData.qr_image}
                    alt="QR Code"
                    className="w-48 h-48"
                    onError={(e) => {
                      console.error("Erreur de chargement du QR code:", e);
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  />
                ) : null}
                <div
                  className="w-48 h-48 bg-gray-100 rounded-lg flex items-center justify-center text-gray-500"
                  style={{ display: qrData.qr_image ? "none" : "flex" }}
                >
                  <div className="text-center">
                    <QrCode className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm">QR Code non disponible</p>
                  </div>
                </div>
              </div>
              <p className="text-gray-600 mb-6 text-sm">
                Présentez ce code aux administrateurs lors des événements
              </p>
              <button
                onClick={() => setShowQRModal(false)}
                className="w-full bg-blue-600 text-white py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentDashboard;
